/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { trackSubscriptionCompleted } from "../utils/analytics";

const SuccessPage: React.FC = () => {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = new URLSearchParams(location.search);
  const sessionId = query.get("session_id");

  useEffect(() => {
    let pollTimer: NodeJS.Timeout | null = null;

    const confirmSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Not authenticated. Please log in again.");
        setLoading(false);
        return;
      }

      // Poll for subscription status (webhook may be delayed)
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000; // 2 seconds

      const checkSubscription = async (): Promise<boolean> => {
        try {
          const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/api/subscriptions/status`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (response.data?.isActive) {
            // Refresh user data to get updated subscription status
            const userResponse = await axios.get(
              `${import.meta.env.VITE_API_URL}/api/auth/me`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setUser(userResponse.data);
            
            // Track subscription completion
            const subscriptionResponse = await axios.get(
              `${import.meta.env.VITE_API_URL}/api/subscriptions/user-subscription`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (subscriptionResponse.data?.subscription) {
              trackSubscriptionCompleted(
                subscriptionResponse.data.subscription.id,
                subscriptionResponse.data.subscription.planType || 'monthly'
              );
            }
            
            return true;
          }
          return false;
        } catch (err: any) {
          console.error('Subscription check error:', err);
          return false;
        }
      };

      // Try immediately
      if (await checkSubscription()) {
        setLoading(false);
        return;
      }

      // Poll if not immediately active
      pollTimer = setInterval(async () => {
        attempts++;
        if (await checkSubscription() || attempts >= maxAttempts) {
          if (pollTimer) clearInterval(pollTimer);
          setLoading(false);
          if (attempts >= maxAttempts && !user?.isSubscribed) {
            setError("Subscription verification timed out. Please refresh the page or contact support.");
          }
        }
      }, pollInterval);
    };

    confirmSubscription();

    // Cleanup function
    return () => {
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [user, setUser]);

  if (loading) return <p>Verifying your subscription...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2>Subscription Successful!</h2>
      <p>
        Your subscription has been activated. You can now access all premium
        features.
      </p>
      <Link
        to="/tasks"
        style={{
          marginTop: "1rem",
          display: "inline-block",
          padding: "0.5rem 1rem",
          backgroundColor: "#4CAF50",
          color: "#fff",
          borderRadius: "4px",
          textDecoration: "none",
        }}
      >
        Go to Tasks
      </Link>
    </div>
  );
};

const styles = {
  container: {
    padding: "2rem",
    textAlign: "center",
  },
  link: {
    marginTop: "1rem",
    display: "inline-block",
    padding: "0.5rem 1rem",
    backgroundColor: "#4CAF50",
    color: "#fff",
    borderRadius: "4px",
    textDecoration: "none",
  },
};

export default SuccessPage;
